import React, { createContext, useContext, useState, useMemo } from "react";
import PropTypes from "prop-types";
import useResponsive from "../hooks/useResponsive";
import { BREAKPOINTS } from "../constants/sideNav";

/**
 * Context for managing SideNav state
 * Provides state management for expanded items, navigation expansion, and panel sizing
 */
const SideNavStateContext = createContext();

/**
 * Hook to access the SideNav context
 *
 * @returns {Object} Context value containing state and setters
 * @throws {Error} When used outside of SideNavStateProvider
 */
export const useSideNavStateContext = () => {
  const context = useContext(SideNavStateContext);
  if (!context) {
    throw new Error(
      "useSideNavStateContext must be used within a SideNavStateProvider"
    );
  }
  return context;
};

/**
 * Provider component for SideNav state management
 *
 * Manages the following state:
 * - expandedItem: Currently expanded navigation item index (null when none)
 * - isExpanded: Whether the sidebar is expanded (collapsed shows icons only)
 * - expandedWidth: Width/height of the expanded panel (desktop: width, mobile: height)
 * - isLocked: Reserved for future use (e.g. lock sidebar open)
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {number} [props.smallScreenBreakpoint] - Width at or below which
 *   the nav uses its compact (bottom-bar) layout. Defaults to 900. The app
 *   raises this by the Ask TeamForm pane width so the Dashboards panel
 *   collapses before the page is crushed.
 * @param {any} [props.initialViewing] - Deprecated; kept for backward compatibility
 * @param {boolean} [props.initialIsExpanded] - First-paint expand/collapse. Defaults to expanded.
 * @returns {JSX.Element} Context provider wrapper
 */
export const SideNavStateProvider = ({
  children,
  smallScreenBreakpoint = BREAKPOINTS.SMALL_SCREEN,
  initialIsExpanded = true,
}) => {
  const [expandedItem, setExpandedItem] = useState(null);
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const [expandedWidth, setExpandedWidth] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const { isSmallScreen } = useResponsive(smallScreenBreakpoint);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      expandedItem,
      setExpandedItem,
      isExpanded,
      setIsExpanded,
      expandedWidth,
      setExpandedWidth,
      isLocked,
      setIsLocked,
      isSmallScreen,
    }),
    [expandedItem, isExpanded, expandedWidth, isLocked, isSmallScreen]
  );

  return (
    <SideNavStateContext.Provider value={value}>
      {children}
    </SideNavStateContext.Provider>
  );
};

SideNavStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
  smallScreenBreakpoint: PropTypes.number,
  initialViewing: PropTypes.any, // Kept for backward compatibility
  initialIsExpanded: PropTypes.bool,
};

export default SideNavStateContext;
